import React, { useEffect, useState } from "react";
import AdminModal from "components/admin/AdminModal";
import { creditTokens } from "apis/adminServices/userManagmentService";
import { useToastStore } from "store/toastStore";
import { hasLifetimeOgRole } from "utils/roles";
import { User } from "./type";

interface CreditTokenModalProps {
  isOpen: boolean;
  users: User[];
  onClose: () => void;
  onCredited: () => void;
}

const MAX_TOKENS = 100000;

const CreditTokenModal: React.FC<CreditTokenModalProps> = ({
  isOpen,
  users,
  onClose,
  onCredited,
}) => {
  const [tokens, setTokens] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToastStore();

  useEffect(() => {
    if (!isOpen) {
      setTokens("");
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const tokenCount = Number(tokens);
  const isValidAmount = tokens !== "" && tokenCount > 0 && tokenCount <= MAX_TOKENS;

  const handleTokensChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTokens(e.target.value.replace(/[^\d]/g, ""));
    setError(null);
  };

  const isUserUnlimited = (user?: User | null): boolean => {
    if (!user) return false;
    return (
      hasLifetimeOgRole(user.userRoles) ||
      user.totalCredits === null ||
      user.totalCredits === undefined
    );
  };

  const eligibleUsers = users.filter((u) => !isUserUnlimited(u));

  const handleSubmit = async () => {
    if (eligibleUsers.length === 0) {
      setError("Selected user(s) already have unlimited tokens and cannot receive additional credits.");
      return;
    }
    if (tokens.trim() === "") {
      setError("Please enter the number of tokens to credit.");
      return;
    }
    if (!isValidAmount) {
      setError(`Enter a token amount between 1 and ${MAX_TOKENS}.`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const result = await creditTokens(
        eligibleUsers.map((user) => user.userId),
        tokenCount
      );

      // A 200 can still carry isSuccess:false; treat it as a failure.
      if (result?.isSuccess === false) {
        setError(result?.message || "Failed to credit tokens. Please try again.");
        return;
      }

      // Partial success (some ids skipped) is reported through `message`, so
      // surface the server's wording verbatim and leave it up longer to read.
      const skipped: number[] = result?.data?.missingUserIds ?? [];
      showToast(
        result?.message || "Tokens successfully credited to selected users.",
        "success",
        skipped.length > 0 ? 6000 : 3000
      );
      onCredited();
      onClose();
    } catch (err: any) {
      // The API reports 400/404/500 through the envelope's `message`, so show it
      // as-is. Network/timeout/rate-limit errors are rewritten by the axios
      // interceptor into plain Errors that already carry a readable message.
      const apiMessage = err?.response?.data?.message;
      const clientMessage =
        err?.isNetworkError || err?.isTimeout || err?.isRateLimited
          ? err.message
          : null;
      setError(
        apiMessage || clientMessage || "Failed to credit tokens. Please try again."
      );
      console.error("Error crediting tokens:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title="Token Credit"
      size="md"
      showCloseButton={false}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 text-sm bg-royalblue-100 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Crediting..." : "Credit"}
          </button>
        </>
      }
    >
      <div>
        {/* Label sits beside the input from sm up, stacks above it on phones
            so neither gets squeezed. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <label
            htmlFor="credit-token-amount"
            className="text-sm text-grease-100 sm:whitespace-nowrap"
          >
            Tokens to Credit :
          </label>
          <input
            id="credit-token-amount"
            type="text"
            inputMode="numeric"
            autoFocus
            className="w-full sm:flex-1 sm:w-auto min-w-0 border border-gray-300 rounded-md p-2 text-sm text-grease-100 focus:outline-none focus:ring-0 focus:border-royalblue-100"
            placeholder="Enter number of tokens"
            value={tokens}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={handleTokensChange}
            disabled={submitting}
          />
        </div>

        <p className="text-xs text-lightgray-200 mt-3">
          Will be added to the balance of {users.length}{" "}
          {users.length === 1 ? "selected user" : "selected users"}.
        </p>

        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
      </div>
    </AdminModal>
  );
};

export default CreditTokenModal;
